window.PS = {
  data: null,
  fluidData: null,
  motorData: null,
  dimensionData: null,
  surfaceData: null,
  ongaDimensionData: null,
  jungDimensionData: null,
  charts: {},

  state: {
    currentMatches: [],
    currentSelectedIndex: 0,
    focusedMatchIndex: null,
    visibleMatchIndexes: new Set(),
    dutyModes: new Set(['head', 'efficiency']),
    detailModes: new Set(['head']),
    duty: {
      flow: 0,
      head: 0,
      controlType: 'fixed',
      fluidId: 'water',
      temperatureC: 20,
      densityKgL: 1,
      viscosityCp: 1
    },
    selectedCategory: null,
    selectedPhase: null,
    selectedApplicationType: null,
    selectedMotorBrand: 'te3',
    selectedMountingType: 'starline',
    selectedMotorPoles: null,
    selectedMotorKw: null,
    // Which supply the motor runs on: 1, 3, or null for "not chosen, use the
    // pump's default". A motor property, not a curve property - a Southern
    // Cross Vertical Multistage stack is hydraulically identical in both
    // builds, so phase decides the motor and the unit code, nothing else.
    // Sticky across pumps like selectedMotorKw, so every read goes through
    // PS.getEffectiveMotorPhase, which re-checks the pump can take it.
    selectedMotorPhase: null,
    speedDisplayUnit: 'hz',
    extraCurveSpecs: [],
    dutyPointVisible: true,
    openAdjustSections: new Set(),
    selectedMaterial: 'CI-CI',
    selectedVariantSeries: 'PT',
    parallelPumpCount: 1,
    dutyPoints: [],
    // Basic vs Advanced view of the selection stage. Basic (the default)
    // hides engineering-only tools (Bearing Load Analysis, System Curve);
    // Advanced reveals them. This is a global preference that persists across
    // pumps, unlike systemCurve below which resets per pump.
    selectionMode: 'basic',
    // System curve overlay: static (lift) head in base units (metres), with
    // the parabola H = staticHead + K*Q^2 fitted so it passes through the
    // duty point. Reset per pump in showDetailView.
    systemCurve: { enabled: false, staticHeadBase: null }
  },

  searchSettings: {
    idealBepWindowPercent: 15,
    maxBepWindowPercent: 35,
    // VSD result ordering: TAPS' own Search Results default (verified from
    // its "Selection Criteria" query) ranks unconditionally by pole count
    // first (2-pole before 4/6/8), then by power - which can put a poorly-
    // fitting fast option ahead of a much tighter-fitting slower one. The
    // default here instead treats any candidate within vsdMaxHeadMarginPercent
    // of the duty head as "genuinely fits", and only lets pole count/power
    // decide within that group - a floor-case candidate outside it always
    // ranks last, regardless of pole count. Setting vsdStrictPoleOrder to
    // true switches to TAPS' own unconditional pole-first behaviour, for
    // staff who specifically want to see it exactly as TAPS would.
    vsdStrictPoleOrder: false,
    vsdMaxHeadMarginPercent: 10
  },

  unitSettings: {
    flow: 'lps',
    head: 'm',
    power: 'kw',
    frequency: 50
  },

  chartModeConfig: {
    head: { label: 'Head', color: '#1f77b4', yAxisID: 'yHead' },
    power: { label: 'Power', color: '#d62728', yAxisID: 'yPower' },
    npsh: { label: 'NPSHr', color: '#2ca02c', yAxisID: 'yNpsh' },
    efficiency: { label: 'Efficiency', color: '#9467bd', yAxisID: 'yEfficiency' }
  },

  // Mirrors TAPS's own "Casing Materials" tables (Matlfactor per Matlcode),
  // keyed by the pump family's categoryCode. Only end-suction categories are
  // listed here - vertical multistage pumps are stainless-only in our range
  // and TAPS itself never derates them for material (see PS.getMaterialFactor).
  materialFactorsByCategory: {
    SCES: { 'CI-CI': 0, 'CI-SS': 0.03, 'SS-SS': 0.06 },
    PWES: { 'CI-CI': 0, 'CI-SS': 0.04, 'SS-SS': 0.06 }
  },

  materialOptions: [
    { value: 'CI-CI', label: 'Cast Iron / Cast Iron' },
    { value: 'CI-SS', label: 'Cast Iron / Stainless Steel' },
    { value: 'SS-SS', label: 'Stainless Steel / Stainless Steel' }
  ]
};

PS.matchColours = [
  '#1f77b4',
  '#ff7f0e',
  '#2ca02c',
  '#d62728',
  '#9467bd',
  '#8c564b',
  '#e377c2',
  '#7f7f7f',
  '#bcbd22',
  '#17becf'
];

PS.getMatchColor = function (index) {
  return PS.matchColours[index % PS.matchColours.length];
};

PS.withAlpha = function (hex, alpha) {
  const clean = String(hex).replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

PS.loadJsonFromUrl = async function (url, label) {
  if (!url) {
    throw new Error(`${label} URL is missing.`);
  }

  const response = await fetch(url, {
    cache: 'no-store'
  });

  if (!response.ok) {
    throw new Error(`${label} could not be loaded: ${response.status}`);
  }

  return response.json();
};

// Each entry is [windowVar, label]. Files are loaded in parallel; missing
// optional files are silently skipped so new brands can be rolled out by
// uploading the JSON first without a code deploy.
PS.MANUFACTURER_DATA_URLS = [
  ['PUMP_SOUTHERN_CROSS_DATA_URL', 'Southern Cross'],
  ['PUMP_ONGA_DATA_URL',           'Onga'],
  ['PUMP_CAPRARI_DATA_URL',        'Caprari'],
  ['PUMP_JUNG_DATA_URL',           'Jung Pumpen'],
];

PS.loadManufacturerData = async function () {
  const results = await Promise.all(
    PS.MANUFACTURER_DATA_URLS.map(async ([varName, label]) => {
      const url = window[varName];
      if (!url) return null;
      try {
        return await PS.loadJsonFromUrl(url, `${label} pump data`);
      } catch (error) {
        console.warn(`${label} pump data could not be loaded:`, error);
        return null;
      }
    })
  );

  // Merge all loaded datasets into a single structure.
  // Southern Cross uses pumpFamilies; domestic brands use a pumps array.
  const merged = { pumpFamilies: [], pumps: [] };
  for (const dataset of results) {
    if (!dataset) continue;
    if (Array.isArray(dataset.pumpFamilies)) {
      merged.pumpFamilies.push(...dataset.pumpFamilies);
    }
    if (Array.isArray(dataset.pumps)) {
      merged.pumps.push(...dataset.pumps);
    }
    // Carry through top-level metadata from the first file that has it
    if (dataset.curveUnits && !merged.curveUnits) merged.curveUnits = dataset.curveUnits;
    if (dataset.powerBasis && !merged.powerBasis) merged.powerBasis = dataset.powerBasis;
  }
  return merged;
};

PS.loadData = async function () {
  const [manufacturerData, fluidData, motorData, dimensionData, surfaceData] = await Promise.all([
    PS.loadManufacturerData(),
    PS.loadJsonFromUrl(window.PUMP_FLUID_DATA_URL, 'Fluid data'),
    PS.loadJsonFromUrl(window.PUMP_MOTOR_DATA_URL, 'Motor data'),
    PS.loadJsonFromUrl(window.PUMP_DIMENSION_DATA_URL, 'Dimension data'),
    window.PUMP_SURFACE_DATA_URL
      ? PS.loadJsonFromUrl(window.PUMP_SURFACE_DATA_URL, 'Surface coefficient data')
      : Promise.resolve(null),
  ]);

  PS.data = manufacturerData;
  PS.fluidData = fluidData;
  PS.motorData = motorData;
  PS.dimensionData = dimensionData;
  PS.surfaceData = surfaceData;

  // Optional files rolled out independently — missing files must not break
  // the rest of the tool.
  PS.cadData = null;
  if (window.PUMP_CAD_DATA_URL) {
    try {
      PS.cadData = await PS.loadJsonFromUrl(window.PUMP_CAD_DATA_URL, 'CAD drawing data');
    } catch (error) {
      console.warn('CAD drawing data could not be loaded:', error);
    }
  }

  PS.bearingCalcData = null;
  if (window.PUMP_BEARING_DATA_URL) {
    try {
      PS.bearingCalcData = await PS.loadJsonFromUrl(window.PUMP_BEARING_DATA_URL, 'Bearing calc data');
    } catch (error) {
      console.warn('Bearing calc data could not be loaded:', error);
    }
  }

  // Onga dimension data (JS/OME etc) - kept in its own file rather than
  // pump-selector-dimension-data.json, which is Southern Cross-only (keyed
  // by TAPS pump code + motor poles/kW for swappable-motor ISO end-suction
  // pumps). Onga's fixed-motor domestic range doesn't fit that schema.
  PS.ongaDimensionData = null;
  if (window.PUMP_ONGA_DIMENSION_DATA_URL) {
    try {
      PS.ongaDimensionData = await PS.loadJsonFromUrl(window.PUMP_ONGA_DIMENSION_DATA_URL, 'Onga dimension data');
    } catch (error) {
      console.warn('Onga dimension data could not be loaded:', error);
    }
  }

  // Jung dimension data - same rationale/shape as Onga's own file above
  // (Jung's fixed-motor submergible range doesn't fit the Southern Cross
  // dimension schema either), kept in its own file since Jung's lettered
  // fields (A-Q) don't match Onga's (A-F/G1/G2/DNA/DNM).
  PS.jungDimensionData = null;
  if (window.PUMP_JUNG_DIMENSION_DATA_URL) {
    try {
      PS.jungDimensionData = await PS.loadJsonFromUrl(window.PUMP_JUNG_DIMENSION_DATA_URL, 'Jung dimension data');
    } catch (error) {
      console.warn('Jung dimension data could not be loaded:', error);
    }
  }

  if (!Array.isArray(PS.fluidData?.fluids)) {
    throw new Error('Fluid data loaded, but it does not contain a fluids array.');
  }

  PS.applyConfig(await PS.loadOptionalConfig());

  // Invalidate the curves cache whenever data is reloaded so getCurvesArray
  // rebuilds fresh normalised objects from the new data.
  PS._curvesCache = null;
};

// Optional - lets the team tune search/chart defaults by editing a JSON
// file in Shopify Files instead of editing code. Missing file or bad JSON
// just means the hardcoded defaults already on PS.searchSettings etc. stay
// in effect, so this must never throw.
PS.loadOptionalConfig = async function () {
  if (!window.PUMP_SELECTOR_CONFIG_URL) return null;

  try {
    return await PS.loadJsonFromUrl(window.PUMP_SELECTOR_CONFIG_URL, 'Selector config');
  } catch (error) {
    console.warn('Selector config could not be loaded, using built-in defaults:', error);
    return null;
  }
};

PS.applyConfig = function (config) {
  if (!config) return;

  if (Number.isFinite(config.recommendedRangeMinPercentOfBep)) {
    PS.recommendedRangeMinPercentOfBep = config.recommendedRangeMinPercentOfBep;
  }

  if (Number.isFinite(config.recommendedRangeMaxPercentOfBep)) {
    PS.recommendedRangeMaxPercentOfBep = config.recommendedRangeMaxPercentOfBep;
  }

  if (Number.isFinite(config.minFlowPercentOfBep)) {
    PS.minFlowPercentOfBep = config.minFlowPercentOfBep;
  }

  Object.assign(PS.searchSettings, config.searchSettings || {});
};

// Which landing-page category tile (PS.state.selectedCategory: 'centrifugal'/
// 'submersible'/'submergible') a curve belongs to, used to scope the duty
// search and pump-type dropdowns so e.g. a Submergible search (Jung) never
// surfaces a Centrifugal pump (SC/Onga/Caprari) and vice versa. Defaults to
// 'centrifugal' when a curve doesn't set it explicitly - every manufacturer
// added so far (Southern Cross, Onga, Caprari) lives under that one tile, so
// this is a no-op for all of them; only a new range that explicitly sets
// landingCategory (e.g. Jung Pumpen: "submergible") opts into a different tile.
PS.getLandingCategory = curve => curve.landingCategory || 'centrifugal';

PS.isDataEntryVisible = function (entry) {
  if (!entry) return true;
  return entry.hidden !== true && entry.visible !== false;
};

PS.normaliseCurve = function (curve, family = {}) {
  if (curve.col_1 !== undefined) {
    return {
      ...curve,
      familyName: curve.col_1,
      model: curve.col_1,
      manufacturer: curve.col_2,
      pumpType: curve.col_3,
      landingCategory: curve.landingCategory || 'centrifugal',
      speedRpm: Number(curve.col_5),
      impellerDiameterMm: Number(curve.col_6),
      stages: Number(curve.col_7 || 1),
      qMaxLps: Number(curve.col_8),

      coefficients: {
        head: [curve.col_10, curve.col_11, curve.col_12, curve.col_13, curve.col_14, curve.col_15].map(Number),
        power: [curve.col_17, curve.col_18, curve.col_19, curve.col_20, curve.col_21, curve.col_22].map(Number),
        npshr: [curve.col_27, curve.col_28, curve.col_29, curve.col_30, curve.col_31, curve.col_32].map(Number),
        efficiency: null
      },

      bep: curve.bep || null,
      shopify: curve.shopify || {}
    };
  }

  // Domestic / fixed-pump format (Onga OME etc): performance coefficients are
  // nested under `performance` and motor data under `motor`. Map them into the
  // flat layout the search engine expects.
  if (curve.performance?.headCoefficients) {
    const perf = curve.performance;
    const mot = curve.motor || {};
    const manufacturer = curve.manufacturer || 'Onga';
    const jungPumpType = String(curve.subcategory || curve.category || '').split(/\s+/)[0];
    const pumpType = manufacturer === 'Jung Pumpen'
      ? (jungPumpType || curve.category || 'Jung')
      : (curve.category || curve.subcategory || 'Domestic');
    return {
      ...curve,
      familyName: curve.model || curve.subcategory,
      model: curve.model,
      manufacturer,
      // pumpType drives the "Pump type" filter dropdown - use the broad,
      // curated group ("Domestic"/"Water Transfer"/"Jet Pumps", see
      // ROADMAP.md's Onga category scheme) rather than the specific series
      // code (subcategory: "OME","JS","112",...), which would keep growing
      // by one dropdown row per series as more Onga pumps are added.
      // Jung uses subcategory values such as "MultiCut DN32"; use just the
      // product family so the Submergible dropdown stays compact.
      // subcategory remains available on the curve for anything that wants
      // the finer-grained series code.
      pumpType,
      landingCategory: curve.landingCategory || 'centrifugal',
      headCoefficients: perf.headCoefficients,
      powerCoefficients: perf.powerCoefficients || null,
      npshCoefficients: perf.npshCoefficients || null,
      efficiencyCoefficients: perf.efficiencyCoefficients || null,
      qMaxLps: perf.validToLps,
      // Most domestic pumps (OME/JS) publish a curve starting at Q=0, so this
      // is 0 for them - but some (112) only publish tested points starting
      // well above zero flow, and fitting/extrapolating the polynomial back
      // to Q=0 produces a physically wrong shutoff head. PS.getCurveQMin
      // already exists and is respected elsewhere; this just wires it up for
      // the domestic curve format too.
      qMinLps: perf.validFromLps || 0,
      bep: perf.bep || (perf.bepFlowLps != null ? { flowLps: perf.bepFlowLps } : null),
      speedRpm: mot.nominalRpm || null,
      stages: 1,
      impellerDiameterMm: null,
      fixedPump: true,
      familyMetadata: {}
    };
  }

  return {
    ...curve,
    familyName: family.family || curve.familyName || curve.model,
    model: family.family || curve.model || curve.familyName,
    manufacturer: curve.manufacturer || family.manufacturer,
    pumpType: curve.pumpType || family.type,
    landingCategory: curve.landingCategory || family.landingCategory || 'centrifugal',
    familyMetadata: { ...(family.metadata || {}), cpSuctionConstants: family.cpSuctionConstants || null, materialVariants: family.materialVariants || null }
  };
};

PS.getCurvesArray = function () {
  if (PS._curvesCache) return PS._curvesCache;

  const result = [];

  if (Array.isArray(PS.data?.pumpFamilies)) {
    result.push(...PS.data.pumpFamilies.flatMap(family =>
      PS.isDataEntryVisible(family)
        ? (family.curves || []).filter(PS.isDataEntryVisible).map(curve => PS.normaliseCurve(curve, family))
        : []
    ));
  } else if (Array.isArray(PS.data?.curves)) {
    result.push(...PS.data.curves.filter(PS.isDataEntryVisible).map(curve => PS.normaliseCurve(curve)));
  }

  if (Array.isArray(PS.data?.pumps)) {
    result.push(...PS.data.pumps.filter(PS.isDataEntryVisible).map(pump => PS.normaliseCurve(pump)));
  }

  PS._curvesCache = result;
  return result;
};

// Curves scoped to whichever landing-page category tile is currently active
// (see PS.getLandingCategory). Falls back to the full list when no category
// is set yet (e.g. dropdown pre-population before a tile has been clicked).
PS.getCurvesArrayForCategory = function () {
  const category = PS.state.selectedCategory;
  const curves = PS.getCurvesArray();
  return category ? curves.filter(curve => PS.getLandingCategory(curve) === category) : curves;
};

PS.getFluidArray = function () {
  return Array.isArray(PS.fluidData?.fluids)
    ? PS.fluidData.fluids
    : [];
};

// Refreshing the page used to throw away the whole duty search - everything
// needed to put the user back where they were is re-derived deterministically
// from a small "recipe" (duty inputs, settings, selections) rather than
// serialising match objects directly, since matches hold a live reference to
// a curve from PS.data and are cheap to regenerate from the same inputs.
PS.SESSION_STORAGE_KEY = 'pumpSelectorSession';

PS.getActiveViewName = function () {
  const isVisible = id => document.getElementById(id)?.style.display === 'block';

  if (isVisible('detail-view')) return 'detail';
  if (isVisible('model-view')) return 'model';
  if (isVisible('duty-results-view')) return 'duty-results';
  return 'duty-input';
};

PS.buildDetailSnapshot = function (match) {
  match = match || PS.state.currentMatches[PS.state.currentSelectedIndex];

  if (!match || match.isVertical) {
    // Vertical multistage "trim" is really a stage count that swaps the
    // underlying curve within the family, not a simple affinity adjustment
    // of one curve - re-deriving that exactly isn't worth it here, so these
    // fall back to the duty-results view on restore instead.
    return null;
  }

  return {
    familyName: match.curve.familyName,
    curveId: match.curve.id,
    flow: match.flow,
    requiredHead: match.requiredHead,
    selectedTrimMm: match.selectedTrimMm,
    selectedFrequencyHz: match.selectedFrequencyHz,
    controlType: match.controlType,
    isAutoDuty: match.isAutoDuty === true
  };
};

PS.saveSession = function () {
  if (!window.sessionStorage) return;

  try {
    const view = PS.getActiveViewName();

    // The model-search flow has its own cascading dropdowns that aren't
    // captured here - leave whatever was last saved untouched rather than
    // overwrite it with an incomplete snapshot.
    if (view === 'model') return;

    if (view === 'detail') {
      const detail = PS.buildDetailSnapshot();
      if (!detail) return;
      PS.writeSessionSnapshot('duty-results', detail);
      return;
    }

    PS.writeSessionSnapshot(view, null);
  } catch (error) {
    console.warn('Could not save pump selector session', error);
  }
};

PS.writeSessionSnapshot = function (view, detail) {
  const snapshot = {
    view: detail ? 'detail' : view,
    dutyForm: {
      flowUnit: document.getElementById('duty-flow-unit')?.value,
      headUnit: document.getElementById('duty-head-unit')?.value,
      flow: document.getElementById('pump-flow')?.value,
      head: document.getElementById('pump-head')?.value,
      suction: document.getElementById('pump-suction')?.value,
      controlType: document.getElementById('control-type')?.value,
      speedRangeMin: document.getElementById('speed-range-min')?.value,
      speedRangeMax: document.getElementById('speed-range-max')?.value,
      poleFilter: ['2', '4', '6', '8'].filter(poles => document.getElementById(`pole-filter-${poles}`)?.checked),
      phase: document.querySelector('input[name="phase-filter"]:checked')?.value || '',
      applicationType: document.querySelector('input[name="application-filter"]:checked')?.value || '',
      fluidId: document.getElementById('fluid-select')?.value,
      temperatureC: document.getElementById('fluid-temperature')?.value,
      densityKgL: document.getElementById('fluid-density')?.value,
      viscosityCp: document.getElementById('fluid-viscosity')?.value
    },
    selectedCategory: PS.state.selectedCategory,
    selectedPhase: PS.state.selectedPhase,
    selectedApplicationType: PS.state.selectedApplicationType,
    unitSettings: PS.unitSettings,
    searchSettings: PS.searchSettings,
    selectedMotorBrand: PS.state.selectedMotorBrand,
    selectedMountingType: PS.state.selectedMountingType,
    selectedMotorPoles: PS.state.selectedMotorPoles,
    selectedMotorKw: PS.state.selectedMotorKw,
    selectedMotorPhase: PS.state.selectedMotorPhase,
    speedDisplayUnit: PS.state.speedDisplayUnit,
    dutyModes: [...PS.state.dutyModes],
    detailModes: [...PS.state.detailModes],
    dutyPointVisible: PS.state.dutyPointVisible,
    openAdjustSections: [...PS.state.openAdjustSections],
    extraCurveSpecs: PS.state.extraCurveSpecs || [],
    parallelPumpCount: PS.state.parallelPumpCount || 1,
    dutyPoints: PS.state.dutyPoints || [],
    selectionMode: PS.state.selectionMode || 'basic',
    systemCurve: PS.state.systemCurve || { enabled: false, staticHeadBase: null },
    results: {
      currentSelectedIndex: PS.state.currentSelectedIndex,
      visibleMatchIndexes: [...PS.state.visibleMatchIndexes],
      focusedMatchIndex: PS.state.focusedMatchIndex
    },
    detail
  };

  window.sessionStorage.setItem(PS.SESSION_STORAGE_KEY, JSON.stringify(snapshot));
};

PS.restoreSession = function () {
  if (!window.sessionStorage) return false;

  let snapshot;

  try {
    const raw = window.sessionStorage.getItem(PS.SESSION_STORAGE_KEY);
    if (!raw) return false;
    snapshot = JSON.parse(raw);
  } catch (error) {
    console.warn('Could not read saved pump selector session', error);
    return false;
  }

  return PS.applySnapshot(snapshot);
};

// Shared by the sessionStorage restore (refresh) and the shareable-URL
// restore (link opened in a fresh tab/browser) - both produce the same
// snapshot shape, just from a different source.
PS.applySnapshot = function (snapshot) {
  if (!snapshot || snapshot.view === 'duty-input') {
    return false;
  }

  try {
    Object.assign(PS.unitSettings, snapshot.unitSettings || {});
    Object.assign(PS.searchSettings, snapshot.searchSettings || {});

    PS.state.selectedCategory = snapshot.selectedCategory || PS.state.selectedCategory;
    PS.state.selectedPhase = snapshot.selectedPhase ?? PS.state.selectedPhase;
    PS.state.selectedApplicationType = snapshot.selectedApplicationType ?? PS.state.selectedApplicationType;
    PS.state.selectedMotorBrand = snapshot.selectedMotorBrand || PS.state.selectedMotorBrand;
    PS.state.selectedMountingType = snapshot.selectedMountingType || PS.state.selectedMountingType;
    PS.state.selectedMotorPoles = snapshot.selectedMotorPoles ?? PS.state.selectedMotorPoles;
    PS.state.selectedMotorKw = snapshot.selectedMotorKw ?? PS.state.selectedMotorKw;
    PS.state.selectedMotorPhase = snapshot.selectedMotorPhase ?? PS.state.selectedMotorPhase;
    PS.state.speedDisplayUnit = snapshot.speedDisplayUnit || PS.state.speedDisplayUnit;
    PS.state.dutyModes = new Set(snapshot.dutyModes && snapshot.dutyModes.length ? snapshot.dutyModes : ['head', 'efficiency']);
    PS.state.detailModes = new Set(snapshot.detailModes && snapshot.detailModes.length ? snapshot.detailModes : ['head']);
    PS.state.dutyPointVisible = snapshot.dutyPointVisible !== false;
    PS.state.openAdjustSections = new Set(snapshot.openAdjustSections || []);
    PS.state.extraCurveSpecs = snapshot.extraCurveSpecs || [];
    PS.state.parallelPumpCount = snapshot.parallelPumpCount || 1;
    PS.state.dutyPoints = snapshot.dutyPoints || [];
    PS.state.selectionMode = snapshot.selectionMode === 'advanced' ? 'advanced' : 'basic';
    PS.state.systemCurve = snapshot.systemCurve && typeof snapshot.systemCurve === 'object'
      ? { enabled: !!snapshot.systemCurve.enabled, staticHeadBase: snapshot.systemCurve.staticHeadBase ?? null }
      : { enabled: false, staticHeadBase: null };

    // Pump-type dropdown options and the Pump application fieldset are both
    // scoped to selectedCategory (see PS.getCurvesArrayForCategory /
    // PS.updateApplicationFilterVisibility) - rebuild them for whichever
    // category this snapshot belongs to before restoring form values into them.
    PS.populatePumpTypeDropdowns?.();
    PS.updateApplicationFilterVisibility?.();

    const form = snapshot.dutyForm || {};
    const setValue = (id, value) => {
      const el = document.getElementById(id);
      if (el && value !== undefined && value !== null) el.value = value;
    };

    setValue('duty-flow-unit', form.flowUnit);
    setValue('duty-head-unit', form.headUnit);
    setValue('pump-flow', form.flow);
    setValue('pump-head', form.head);
    setValue('pump-suction', form.suction);
    setValue('control-type', form.controlType);
    setValue('speed-range-min', form.speedRangeMin);
    setValue('speed-range-max', form.speedRangeMax);

    ['2', '4', '6', '8'].forEach(poles => {
      const el = document.getElementById(`pole-filter-${poles}`);
      if (el) el.checked = (form.poleFilter || []).includes(poles);
    });

    const savedPhase = form.phase || '';
    const phaseRadio = document.querySelector(`input[name="phase-filter"][value="${savedPhase}"]`);
    if (phaseRadio) phaseRadio.checked = true;

    const savedApplicationType = form.applicationType || '';
    if (savedApplicationType) {
      const applicationRadio = document.querySelector(`input[name="application-filter"][value="${savedApplicationType}"]`);
      if (applicationRadio) applicationRadio.checked = true;
    }

    PS.wireSpeedRangeVisibility?.();

    setValue('fluid-select', form.fluidId);
    setValue('fluid-temperature', form.temperatureC);

    // updateFluidProperties recomputes density/viscosity from the fluid's
    // lookup table at this temperature, which would silently discard a
    // manually-typed override - reapply the saved values afterwards so a
    // custom density/viscosity survives the restore.
    PS.updateFluidProperties();
    setValue('fluid-density', form.densityKgL);
    setValue('fluid-viscosity', form.viscosityCp);

    const duty = PS.captureDutyInputs();

    if (!Number.isFinite(duty.flow) || !Number.isFinite(duty.totalHead) || duty.flow <= 0 || duty.totalHead <= 0) {
      return false;
    }

    const matches = PS.findPumpMatches(duty.flow, duty.totalHead, duty.controlType);

    if (!matches.length) {
      return false;
    }

    PS.renderMatches(matches);

    const results = snapshot.results || {};
    const visibleIndexes = Array.isArray(results.visibleMatchIndexes) && results.visibleMatchIndexes.length
      ? results.visibleMatchIndexes.filter(index => PS.state.currentMatches[index])
      : [0];

    PS.state.visibleMatchIndexes = new Set(visibleIndexes.length ? visibleIndexes : [0]);
    PS.state.currentSelectedIndex = PS.state.currentMatches[results.currentSelectedIndex]
      ? results.currentSelectedIndex
      : 0;
    PS.state.focusedMatchIndex = PS.state.currentMatches[results.focusedMatchIndex] ? results.focusedMatchIndex : null;

    PS.updateMatchButtonStates();
    PS.showDutyResultsView();
    PS.drawChart(
      'pump-chart',
      'duty',
      PS.state.currentMatches,
      PS.state.visibleMatchIndexes,
      PS.state.dutyModes,
      PS.state.currentSelectedIndex
    );
    PS.renderSearchSummary();

    if (snapshot.view === 'detail' && snapshot.detail) {
      PS.restoreDetailView(snapshot.detail);
    }

    return true;
  } catch (error) {
    console.warn('Could not restore pump selector session', error);
    return false;
  }
};

PS.restoreDetailView = function (detail) {
  const curve = PS.getCurvesArray().find(
    item => item.familyName === detail.familyName && item.id === detail.curveId
  );

  if (!curve) return;

  let match = PS.evaluateAdjustedCurve(curve, detail.flow, detail.requiredHead, {
    frequencyHz: detail.selectedFrequencyHz,
    trimMm: detail.selectedTrimMm,
    controlType: detail.controlType
  });

  if (!match) return;

  match.isAutoDuty = detail.isAutoDuty;

  PS.state.currentMatches[PS.state.currentSelectedIndex] = match;

  // showDetailView resets dutyPointVisible/openAdjustSections/extraCurveSpecs
  // to their fresh-selection defaults, so capture our restored values and
  // reapply them afterwards rather than letting it clobber them.
  const restoredDutyPointVisible = PS.state.dutyPointVisible;
  const restoredOpenAdjustSections = PS.state.openAdjustSections;
  const restoredExtraCurveSpecs = PS.state.extraCurveSpecs;
  const restoredDutyPoints = PS.state.dutyPoints;
  const restoredSystemCurve = PS.state.systemCurve;

  PS.showDetailView();

  PS.state.dutyPointVisible = restoredDutyPointVisible;
  PS.state.openAdjustSections = restoredOpenAdjustSections;
  PS.state.extraCurveSpecs = restoredExtraCurveSpecs;
  PS.state.dutyPoints = restoredDutyPoints;
  PS.state.systemCurve = restoredSystemCurve;

  const refreshedMatch = PS.state.currentMatches[PS.state.currentSelectedIndex];

  PS.drawDetailChart('detail-chart', 'detail', refreshedMatch, PS.state.detailModes);
  PS.renderDetailSummary('detail-summary', refreshedMatch, 'detail-pump-title');
  PS.renderAdjustmentPanel('detail-adjustment-panel', refreshedMatch, 'detail-chart', 'detail', 'detail-summary');
  PS.updateChartModeAvailability(refreshedMatch, 'detail');
};

// Shareable-link version of the same snapshot mechanism: instead of stashing
// it in sessionStorage, the snapshot is packed into a single query param so
// the result (a specific pump, at a specific duty/trim/speed) can be sent to
// someone else, or printed on a curve sheet, and reopened on any device.
//
// Two params are recognised on restore:
//   - `p`    : the compact, base64url-encoded snapshot used for all NEW links
//   - `pump` : the legacy raw-JSON param, kept ONLY so links in already-printed
//              PDFs / old bookmarks still reopen.
// New links use `p` because the old `pump=<raw JSON>` form produced a very long,
// heavily percent-encoded URL. As the snapshot schema grew over time that URL
// crossed the length at which some PDF pipelines silently drop the clickable
// link annotation - the "printed link no longer works" the change targets.
// base64url is URL-safe (A-Za-z0-9-_), so `p` is never percent-encoded, and
// pruning default fields keeps it short (see PS.encodeShareParam).
PS.URL_SHARE_PARAM = 'pump';
PS.URL_SHARE_PARAM_COMPACT = 'p';

// UTF-8-safe base64url (no padding). familyName/curveId are ASCII today, but
// encoding via TextEncoder means a future non-ASCII pump name can't corrupt the
// link.
PS.b64urlEncode = function (str) {
  const bytes = new TextEncoder().encode(str);
  let bin = '';
  bytes.forEach(b => { bin += String.fromCharCode(b); });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

PS.b64urlDecode = function (encoded) {
  let b64 = String(encoded).replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';
  const bin = atob(b64);
  const bytes = Uint8Array.from(bin, ch => ch.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

// Prunes a full snapshot down to just the fields that differ from applySnapshot's
// own defaults, then base64url-encodes it. applySnapshot fills every omitted
// field from its defaults, so dropping default-valued fields is lossless on
// restore while cutting the encoded length substantially. dutyForm + detail are
// always kept (they carry the actual duty/pump identity a link exists to convey).
PS.encodeShareParam = function (snapshot) {
  const p = {
    view: snapshot.view,
    dutyForm: snapshot.dutyForm,
    detail: snapshot.detail
  };

  // Carry only non-default values. applySnapshot restores every omitted field
  // from the same defaults, so this is lossless. Dropping the (usually
  // untouched) unit/search-settings objects and the 'te3'/'starline'/'hz'
  // scalars is where most of the length saving comes from.
  ['selectedCategory', 'selectedPhase', 'selectedApplicationType',
   'selectedMotorPoles', 'selectedMotorKw', 'selectedMotorPhase'].forEach(key => {
    if (snapshot[key] != null) p[key] = snapshot[key];
  });

  if (snapshot.selectedMotorBrand && snapshot.selectedMotorBrand !== 'te3') p.selectedMotorBrand = snapshot.selectedMotorBrand;
  if (snapshot.selectedMountingType && snapshot.selectedMountingType !== 'starline') p.selectedMountingType = snapshot.selectedMountingType;
  if (snapshot.speedDisplayUnit && snapshot.speedDisplayUnit !== 'hz') p.speedDisplayUnit = snapshot.speedDisplayUnit;

  const DEFAULT_UNITS = JSON.stringify({ flow: 'lps', head: 'm', power: 'kw', frequency: 50 });
  if (snapshot.unitSettings && JSON.stringify(snapshot.unitSettings) !== DEFAULT_UNITS) p.unitSettings = snapshot.unitSettings;

  const DEFAULT_SEARCH = JSON.stringify({ idealBepWindowPercent: 15, maxBepWindowPercent: 35, vsdStrictPoleOrder: false, vsdMaxHeadMarginPercent: 10 });
  if (snapshot.searchSettings && JSON.stringify(snapshot.searchSettings) !== DEFAULT_SEARCH) p.searchSettings = snapshot.searchSettings;

  const dutyModes = snapshot.dutyModes || [];
  if (!(dutyModes.length === 2 && dutyModes.includes('head') && dutyModes.includes('efficiency'))) {
    p.dutyModes = dutyModes;
  }

  const detailModes = snapshot.detailModes || [];
  if (!(detailModes.length === 1 && detailModes[0] === 'head')) {
    p.detailModes = detailModes;
  }

  if (snapshot.dutyPointVisible === false) p.dutyPointVisible = false;
  if ((snapshot.parallelPumpCount || 1) !== 1) p.parallelPumpCount = snapshot.parallelPumpCount;
  if ((snapshot.dutyPoints || []).length) p.dutyPoints = snapshot.dutyPoints;
  if (snapshot.selectionMode === 'advanced') p.selectionMode = 'advanced';
  if (snapshot.systemCurve && snapshot.systemCurve.enabled) p.systemCurve = snapshot.systemCurve;

  return PS.b64urlEncode(JSON.stringify(p));
};

PS.buildShareableSnapshot = function (match) {
  const detail = PS.buildDetailSnapshot(match);

  return {
    view: detail ? 'detail' : 'duty-results',
    dutyForm: {
      flowUnit: document.getElementById('duty-flow-unit')?.value,
      headUnit: document.getElementById('duty-head-unit')?.value,
      flow: document.getElementById('pump-flow')?.value,
      head: document.getElementById('pump-head')?.value,
      suction: document.getElementById('pump-suction')?.value,
      controlType: document.getElementById('control-type')?.value,
      speedRangeMin: document.getElementById('speed-range-min')?.value,
      speedRangeMax: document.getElementById('speed-range-max')?.value,
      poleFilter: ['2', '4', '6', '8'].filter(poles => document.getElementById(`pole-filter-${poles}`)?.checked),
      phase: document.querySelector('input[name="phase-filter"]:checked')?.value || '',
      applicationType: document.querySelector('input[name="application-filter"]:checked')?.value || '',
      fluidId: document.getElementById('fluid-select')?.value,
      temperatureC: document.getElementById('fluid-temperature')?.value,
      densityKgL: document.getElementById('fluid-density')?.value,
      viscosityCp: document.getElementById('fluid-viscosity')?.value
    },
    selectedCategory: PS.state.selectedCategory,
    selectedPhase: PS.state.selectedPhase,
    selectedApplicationType: PS.state.selectedApplicationType,
    unitSettings: PS.unitSettings,
    searchSettings: PS.searchSettings,
    selectedMotorBrand: PS.state.selectedMotorBrand,
    selectedMountingType: PS.state.selectedMountingType,
    selectedMotorPoles: PS.state.selectedMotorPoles,
    selectedMotorKw: PS.state.selectedMotorKw,
    selectedMotorPhase: PS.state.selectedMotorPhase,
    speedDisplayUnit: PS.state.speedDisplayUnit,
    dutyModes: [...PS.state.dutyModes],
    detailModes: [...PS.state.detailModes],
    dutyPointVisible: PS.state.dutyPointVisible,
    parallelPumpCount: PS.state.parallelPumpCount || 1,
    dutyPoints: PS.state.dutyPoints || [],
    selectionMode: PS.state.selectionMode || 'basic',
    systemCurve: PS.state.systemCurve || { enabled: false, staticHeadBase: null },
    results: {
      currentSelectedIndex: 0,
      visibleMatchIndexes: [0],
      focusedMatchIndex: null
    },
    detail
  };
};

// Builds a full link back to this pump selector page that reopens the given
// match exactly as it is now (same duty, fluid, trim/speed) - vertical
// multistage matches fall back to a duty-results link since their "trim" is
// really a stage count tied to a specific curve within the family (see
// buildDetailSnapshot).
PS.buildShareableUrl = function (match) {
  const snapshot = PS.buildShareableSnapshot(match);
  const params = new URLSearchParams();

  params.set(PS.URL_SHARE_PARAM_COMPACT, PS.encodeShareParam(snapshot));

  const base = `${window.location.origin}${window.location.pathname}`;
  return `${base}?${params.toString()}`;
};

PS.restoreFromUrl = function () {
  const params = new URLSearchParams(window.location.search);
  const compact = params.get(PS.URL_SHARE_PARAM_COMPACT);
  const legacy = params.get(PS.URL_SHARE_PARAM);

  if (!compact && !legacy) return false;

  let snapshot;

  try {
    snapshot = compact ? JSON.parse(PS.b64urlDecode(compact)) : JSON.parse(legacy);
  } catch (error) {
    console.warn('Could not read shared pump selector link', error);
    return false;
  }

  return PS.applySnapshot(snapshot);
};
